import React, { memo, useCallback, useState, useEffect } from 'react';
import CoordinationClient from '@atlassian/jira-engagement/src/ui/coordination-client/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { useLeftSidebarState } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/left-sidebar-controller/index.tsx';
import { useJiraServiceManagementProjectNavigation } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/common/project-sidebar-navigation-resource/index.tsx';
import InvitePeopleModalAsync from '@atlassian/jira-project-invite-people-modal/async.tsx';
import { usePageContext } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/page-context/main.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { useRouter } from '@atlassian/react-resource-router';
import {
	INVITE_USERS_PROMPTS_MESSAGE_ID,
	JSM_ADOPTION_NUDGES_PACKAGE,
	JSM_GETTING_STARTED_ROUTE_NAME,
} from '../common/constants.tsx';
import type { CoordinationStopProviderProps, StopFunction } from '../common/types.tsx';
import InviteUsersPromptNudge from './invite-user-prompt/index.tsx';

const CoordinationStopProvider = ({ stop, children }: CoordinationStopProviderProps) =>
	children(stop);

export const InviteUserPrompt = memo(({ onCTAClick }: { onCTAClick: () => void }) => {
	const [showSpotlight, setShowSpotlight] = useState(false);
	const [spotlightActive, setSpotlightActive] = useState(false);
	const [{ data: pageContextData, loading: pageContextLoading }] = usePageContext();
	const { loading: sideBarNavLoading } = useJiraServiceManagementProjectNavigation();
	const { isCollapsed } = useLeftSidebarState();
	const { projectContext } = pageContextData || {};
	const { projectId, projectName, projectKey } = projectContext || {};

	const { serviceDesk: jsmEdition } = useAppEditions();
	const isSiteAdmin = useIsSiteAdmin();
	const cloudId = useCloudId();
	const [isInviteTeamDialogOpen, setIsInviteTeamDialogOpen] = useState(false);

	const onOpenAddPeopleDialog = useCallback((): void => {
		setIsInviteTeamDialogOpen(true);
	}, [setIsInviteTeamDialogOpen]);

	const onCloseAddPeopleDialog = useCallback((): void => {
		setIsInviteTeamDialogOpen(false);
	}, [setIsInviteTeamDialogOpen]);

	const onFinish = useCallback(
		(stop?: StopFunction) => () => {
			setShowSpotlight(false);
			stop && stop();
		},
		[setShowSpotlight],
	);

	useEffect(() => {
		const id = setTimeout(() => {
			setShowSpotlight(true);
		}, 2000);
		return () => {
			clearTimeout(id);
		};
	}, []);

	const [
		{
			route: { name: routeName },
		},
	] = useRouter() || [{ route: { name: '' } }];

	const shouldSuppressSpotlightInGettingStarted = routeName === JSM_GETTING_STARTED_ROUTE_NAME;

	useEffect(() => {
		if (
			!pageContextLoading &&
			!isCollapsed &&
			!sideBarNavLoading &&
			!shouldSuppressSpotlightInGettingStarted
		) {
			setSpotlightActive(true);
		}
	}, [isCollapsed, pageContextLoading, sideBarNavLoading, shouldSuppressSpotlightInGettingStarted]);

	const onClick = expValEquals('jsm_core_invites_experiment', 'cohort', 'experiment')
		? onOpenAddPeopleDialog
		: onCTAClick;

	return (
		<JSErrorBoundary id="contextual-invite-users-prompt" packageName={JSM_ADOPTION_NUDGES_PACKAGE}>
			{spotlightActive && showSpotlight ? (
				<CoordinationClient messageId={INVITE_USERS_PROMPTS_MESSAGE_ID}>
					<CoordinationStopProvider>
						{(stop) => (
							<InviteUsersPromptNudge
								props={{
									onFinish: onFinish(stop),
									onCTAClick: onClick,
								}}
								entryPoints={{}}
								extraProps={{}}
								queries={{}}
							/>
						)}
					</CoordinationStopProvider>
				</CoordinationClient>
			) : null}
			{isInviteTeamDialogOpen &&
				projectId &&
				expValEquals('jsm_core_invites_experiment', 'cohort', 'experiment') && (
					<InvitePeopleModalAsync
						appEdition={jsmEdition}
						cloudId={cloudId}
						isOpen={isInviteTeamDialogOpen}
						onClose={onCloseAddPeopleDialog}
						projectId={projectId}
						jiraProjectKey={projectKey}
						jiraProjectName={projectName}
						isSiteAdmin={isSiteAdmin}
					/>
				)}
		</JSErrorBoundary>
	);
});
